import { Component, OnInit, ViewChild } from '@angular/core';
import { DataService } from '../../../services/data/data.service';
import { TranslateService } from '@ngx-translate/core';
import { typesApex, FiltersM } from '../../../components/models';
import { GraficadorComponent } from '../../../components/graficador/graficador.component';
import { Meses } from '../../../models/Formulacion/indicadores-metas/datos';
import { Aligment, ExcelService, FormatText } from '../../../services/excel/excel.service';
import {
  AnonymousResultPeriod,
  AnonymousResults,
  AnonymousResultsCross,
  CrossCategory,
  ResultadosClimaService,
} from '../../../services/cuestionarios/resultados-clima/resultados-clima.service';

@Component({
  selector: 'app-analisis-combinado',
  templateUrl: './analisis-combinado.component.html',
  styleUrl: './analisis-combinado.component.css'
})
export class AnalisisCombinadoComponent implements OnInit {
  public types = typesApex;
  public Filters = FiltersM;
  public colorMulti = ['#5EAEE5', '#8AD2E6', '#A4D55D', '#FFE370', '#FFAE5F', '#FB7598'];

  // Catálogos de filtros
  public periodos: any[] = [];
  public dimensiones: { name: string; check: boolean }[] = [];
  public variables1: { key: string; name: string; check: boolean; disabled: boolean }[] = [];
  public variables2: { key: string; name: string; check: boolean; disabled: boolean }[] = [];

  // Selección actual (Sel = etiqueta visible, Key = key demográfica del back)
  public evalSel = '';
  public var1Sel = '';
  public var2Sel = '';
  public dimSel = '';
  private var1Key = '';
  private var2Key = '';

  // Backend
  private institutionId = '';
  private questionnaireId = '';
  /** Máximo real de ponderación de la institución (Instituciones.maximoClimaLaboral); para el % de formatoValor */
  public maximoPonderacion = 10;
  private allocationSelect: number | null = null;
  private periodosPropios: AnonymousResultPeriod[] = [];
  public resultado: AnonymousResults | null = null;
  public cruce: AnonymousResultsCross | null = null;
  public loadingResultados = false;

  // Datos del tablero (null = sin dato)
  public filas: string[] = [];
  public columnas: string[] = [];
  public matriz: (number | null)[][] = [];
  public global: (number | null)[] = [];
  public promedio: (number | null)[] = [];
  public promedioGlobal: number | null = null;

  // Semáforo (rangos reales de la institución, escala 0–10)
  public semaforos: { min: number; max: number; color: string }[] = [];
  public semaforoFiltro = 'todos';

  // 'calificacion' (0-10) | 'porcentaje' (0-100)
  public metrica = 'calificacion';

  // Gráfica
  public chartData: any[] = [];
  public chartLabels: string[] = [];
  public legendGraf = { enabled: true, position: 'bottom' };
  public etiquetasZona1 = true;
  public type: string = typesApex.LINE;

  // Cards mejor/peor
  public cardsTabla: any[] = [];
  public cardsGrafica: any[] = [];

  public ampliarTabla = false;
  public ampliarGrafica = false;

  @ViewChild('graficador') graficador: GraficadorComponent;

  constructor(
    public dataService: DataService,
    public translate: TranslateService,
    private resultadosService: ResultadosClimaService,
    private excelService: ExcelService,
  ) {}

  ngOnInit(): void {
    this.cargarSemaforos();
    this.construirTablero();
    this.cargarPeriodos();
  }

  private async cargarPeriodos(): Promise<void> {
    const cues = JSON.parse(localStorage.getItem('cuestionario') || 'null');
    const allocation = JSON.parse(localStorage.getItem('allocation') || 'null');
    this.questionnaireId = cues?.id != null ? String(cues.id) : '';
    this.institutionId = allocation?.['institution_id'] ?? '';
    this.maximoPonderacion = this.resultadosService.maximoEscala(this.institutionId);

    if (!this.institutionId) {
      this.periodos = [];
      return;
    }

    let periods: AnonymousResultPeriod[] = [];
    try {
      periods = await this.resultadosService.getPeriods(this.institutionId);
    } catch {
      this.periodos = [];
      return;
    }

    const propios = (periods || []).filter(p => String(p.questionnaireId) === this.questionnaireId);
    this.periodosPropios = [...propios].sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
    this.construirFiltroPeriodos(propios);

    // El back viene ordenado por startDate desc: el primero es el más reciente
    const reciente = propios[0];
    if (reciente) {
      this.allocationSelect = reciente.allocationId;
      this.evalSel = this.etiquetaPeriodo(reciente);
      this.marcarPeriodo(reciente.allocationId);
      await this.cargarResultados(reciente.allocationId);
    } else {
      this.allocationSelect = null;
      this.resultado = null;
    }
  }

  /** Agrupa los periodos por año → series de meses para app-filters PERIOD */
  private construirFiltroPeriodos(periods: AnonymousResultPeriod[]): void {
    const porAnio = new Map<string, any>();
    for (const p of periods) {
      const [y, m] = (p.startDate || '').split('-');
      if (!y || !m) continue;
      if (!porAnio.has(y)) {
        porAnio.set(y, { anio: Number(y), visible: true, utilizable: true, series: [] });
      }
      const mesKey = Meses.getMesesNumbertoString(Number(m), true);
      porAnio.get(y).series.push({
        mes: mesKey,
        mesS: mesKey,
        anio: y.substring(2),
        label: `${mesKey} ${y.substring(2)}`,
        periodo: String(p.allocationId),
        allocationId: p.allocationId,
        startDate: p.startDate,
        check: false,
        visible: true,
        utilizable: true,
      });
    }
    const arr = Array.from(porAnio.values()).sort((a, b) => a.anio - b.anio);
    arr.forEach(a => a.series.sort((x: any, y: any) => x.startDate.localeCompare(y.startDate)));
    this.periodos = arr;
  }

  private marcarPeriodo(allocationId: number): void {
    this.periodos.forEach(anio => {
      anio.series.forEach((per: any) => (per.check = per.allocationId === allocationId));
    });
  }

  private etiquetaPeriodo(p: AnonymousResultPeriod): string {
    const [y, m] = (p.startDate || '').split('-');
    if (!y || !m) return p.startDate || '';
    return `${Meses.getMesesNumbertoString(Number(m), true)} ${y.substring(2)}`;
  }

  private async cargarResultados(allocationId: number): Promise<void> {
    this.loadingResultados = true;
    try {
      this.resultado = await this.resultadosService.getResults(allocationId, this.institutionId);
    } catch {
      this.resultado = null;
    }
    this.poblarCatalogos();
    await this.cargarCruce();
    this.loadingResultados = false;
  }

  /** Trae el cruce para las keys/allocation actuales. El spinner lo controla quien la invoca. */
  private async cargarCruce(): Promise<void> {
    const alloc = this.allocationSelect;
    if (alloc == null || !this.var1Key || !this.var2Key) {
      this.cruce = null;
      this.construirTablero();
      return;
    }
    try {
      this.cruce = await this.resultadosService.getCross(alloc, this.institutionId, this.var1Key, this.var2Key);
    } catch {
      this.cruce = null;
    }
    this.construirTablero();
  }

  /**
   * Puebla Variable 1/2 (ejes demográficos) y Dimensión (parentCategoryName distintos)
   * desde el resultado. Conserva la selección previa si sigue existiendo; si no, la primera.
   */
  private poblarCatalogos(): void {
    const res = this.resultado;
    if (!res) {
      this.variables1 = [];
      this.variables2 = [];
      this.dimensiones = [];
      return;
    }

    const ejes = (res.demographics || [])
      .map(d => ({
        key: d.key,
        name: this.catalogoLabel(d.key, d.label),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));

    // Default de la primera carga: sucursal x rango de edad (si existen); si no, la primera/segunda alfabética.
    const key1 = ejes.some(e => e.key === this.var1Key)
      ? this.var1Key
      : (ejes.find(e => e.key === 'branch-office')?.key ?? ejes[0]?.key ?? '');
    let key2 = ejes.some(e => e.key === this.var2Key) ? this.var2Key : '';
    if (!key2) {
      key2 = ejes.find(e => e.key === 'age-range' && e.key !== key1)?.key
        ?? ejes.find(e => e.key !== key1)?.key
        ?? key1;
    }

    this.var1Key = key1;
    this.var2Key = key2;
    this.variables1 = ejes.map(e => ({ ...e, check: e.key === key1, disabled: false }));
    this.variables2 = ejes.map(e => ({ ...e, check: e.key === key2, disabled: false }));
    this.var1Sel = ejes.find(e => e.key === key1)?.name ?? '';
    this.var2Sel = ejes.find(e => e.key === key2)?.name ?? '';
    this.actualizarDeshabilitadas();

    const dims: string[] = [];
    for (const c of res.categories || []) {
      const p = c.parentCategoryName;
      if (p && !dims.includes(p)) dims.push(p);
    }
    const dimSel = dims.includes(this.dimSel) ? this.dimSel : (dims[0] ?? '');
    this.dimSel = dimSel;
    this.dimensiones = dims.map(name => ({ name, check: name === dimSel }));
  }

  private catalogoLabel(key: string, fallback: string): string {
    const ruta = 'cues.cuestionariov2.catalogos.' + key;
    const traduccion = this.translate.instant(ruta);
    // instant() regresa la misma ruta cuando no encuentra la clave
    const traducida = traduccion && traduccion !== ruta;
    return traducida ? traduccion : (fallback || key);
  }

  public async yearChange(event: any): Promise<void> {
    const allocationId = event?.allocationId;
    if (allocationId == null) return;
    this.allocationSelect = allocationId;
    this.evalSel = event.label ?? this.evalSel;
    this.marcarPeriodo(allocationId);
    await this.cargarResultados(allocationId);
  }

  /** Arma el tablero con el cruce de la dimensión activa (ya viene agregado por dimensión). */
  private construirTablero(): void {
    const dimension = this.cruce?.categories.find(c => c.categoryName === this.dimSel);
    if (!dimension) {
      this.vaciarTablero();
      this.armarGrafica();
      this.armarCards();
      return;
    }

    this.filas = dimension.marginalsA.map(m => m.value);
    this.columnas = dimension.marginalsB.map(m => m.value);
    this.matriz = this.armarMatriz(dimension);

    // Totales ya ponderados por el back: no se re-promedian
    this.global = dimension.marginalsA.map(m => m.averageScore);
    this.promedio = dimension.marginalsB.map(m => m.averageScore);
    this.promedioGlobal = dimension.overallAverageScore;

    this.armarGrafica();
    this.armarCards();
  }

  /** Ubica cada celda en [fila][columna]. Combinación no recibida del back = null (sin dato). */
  private armarMatriz(dimension: CrossCategory): (number | null)[][] {
    const puntajePorCelda = new Map<string, number | null>();
    for (const celda of dimension.cells) {
      puntajePorCelda.set(this.claveCelda(celda.valueA, celda.valueB), celda.averageScore);
    }
    return this.filas.map(fila =>
      this.columnas.map(columna => puntajePorCelda.get(this.claveCelda(fila, columna)) ?? null)
    );
  }

  private claveCelda(fila: string, columna: string): string {
    return `${fila}||${columna}`;
  }

  private vaciarTablero(): void {
    this.filas = [];
    this.columnas = [];
    this.matriz = [];
    this.global = [];
    this.promedio = [];
    this.promedioGlobal = null;
  }

  private armarGrafica(): void {
    this.chartLabels = [...this.columnas];
    this.chartData = this.filas.map((nombre, i) => ({
      name: nombre,
      data: [...this.matriz[i]],
    }));
  }

  private armarCards(): void {
    this.cardsTabla = [
      this.mejorPeor(this.columnas, this.promedio, 'up'),
      this.mejorPeor(this.columnas, this.promedio, 'down'),
    ];
    this.cardsGrafica = [
      this.mejorPeor(this.filas, this.global, 'up'),
      this.mejorPeor(this.filas, this.global, 'down'),
    ];
  }

  /** Mejor ('up') o peor ('down') por puntaje, ignorando los sin dato. */
  private mejorPeor(
    nombres: string[],
    puntajes: (number | null)[],
    tipo: 'up' | 'down',
  ): { name: string; score: number | null; tipo: 'up' | 'down' } {
    let elegido = -1;
    puntajes.forEach((puntaje, i) => {
      if (puntaje === null) return;
      if (elegido === -1) {
        elegido = i;
        return;
      }
      const actual = puntajes[elegido] as number;
      const gana = tipo === 'up' ? puntaje > actual : puntaje < actual;
      if (gana) elegido = i;
    });

    if (elegido === -1) return { name: '', score: null, tipo };
    return { name: nombres[elegido], score: puntajes[elegido], tipo };
  }

  private cargarSemaforos(): void {
    const cues = JSON.parse(localStorage.getItem('cuestionario') || 'null');
    if (!cues?.semaphores?.length) {
      this.semaforos = [];
      return;
    }
    const allocation = JSON.parse(localStorage.getItem('allocation') || 'null');
    const semaforos = [...cues.semaphores]
      .map((s: any) => ({
        min: Number(s.value_1),
        max: Number(s.value_2),
        color: s.color,
      }))
      .sort((a, b) => b.min - a.min);
    this.semaforos = this.resultadosService.escalarSemaforos(semaforos, allocation?.['institution_id'] ?? '');
  }

  /** Solo para mostrar el rango del semáforo; semaforoDe() sigue usando el valor exacto */
  public redondeoSemaforo(valor: number): number {
    //DESACTIVADO MOMENTANEAMENTE
    /* return Math.round(valor); */
    return valor;
  }

  private semaforoDe(valor: number | null) {
    if (valor == null || !this.semaforos.length) return null;
    const exacto = this.semaforos.find(s => s.min <= valor && valor <= s.max);
    if (exacto) return exacto;
    // Con huecos entre rangos, cae al inmediato inferior
    const inferiores = this.semaforos.filter(s => valor >= s.min);
    if (inferiores.length) return inferiores.reduce((a, b) => (b.min > a.min ? b : a));
    return this.semaforos.reduce((a, b) => (b.min < a.min ? b : a));
  }

  public estiloSemaforo(valor: number | null): { [k: string]: string } {
    const sem = this.semaforoDe(valor);
    if (!sem) return {};
    if (this.semaforoFiltro === 'todos' || this.semaforoFiltro === sem.color) {
      return { 'border-right': `5px solid ${sem.color}` };
    }
    return {};
  }

  public cambioSemaforo(filtro: string): void {
    this.semaforoFiltro = filtro;
  }

  public cambioMetrica(metrica: string): void {
    this.metrica = metrica;
  }

  public formatoValor(valor: number | null): string {
    if (valor == null) return '—';
    if (this.metrica === 'porcentaje') {
      return `${(valor / this.maximoPonderacion * 100).toFixed(1)} %`;
    }
    return valor.toFixed(2);
  }

  // Deshabilita en cada filtro la variable ya elegida en el otro (no repetir eje)
  private actualizarDeshabilitadas(): void {
    this.variables1.forEach(v => (v.disabled = v.key === this.var2Key));
    this.variables2.forEach(v => (v.disabled = v.key === this.var1Key));
  }

  public async seleccionarVariable1(item: any): Promise<void> {
    if (!item || item.check || item.disabled) return;
    this.variables1.forEach(v => (v.check = v === item));
    this.var1Key = item.key;
    this.var1Sel = item.name;
    this.actualizarDeshabilitadas();
    this.loadingResultados = true;
    await this.cargarCruce();
    this.loadingResultados = false;
  }

  public async seleccionarVariable2(item: any): Promise<void> {
    if (!item || item.check || item.disabled) return;
    this.variables2.forEach(v => (v.check = v === item));
    this.var2Key = item.key;
    this.var2Sel = item.name;
    this.actualizarDeshabilitadas();
    this.loadingResultados = true;
    await this.cargarCruce();
    this.loadingResultados = false;
  }

  public seleccionarDimension(item: any): void {
    if (!item || item.check) return;
    this.dimensiones.forEach(d => (d.check = d === item));
    this.dimSel = item.name;
    this.construirTablero();
  }

  public abrirTabla(): void { this.ampliarTabla = true; }
  public abrirGrafica(): void { this.ampliarGrafica = true; }
  public cerrarModales(): void {
    this.ampliarTabla = false;
    this.ampliarGrafica = false;
  }

  /** Menú 3 puntos de la zona gráfica: cambia el tipo (barras/línea/área) */
  public cambiarTipoGrafica(tipo: string): void {
    this.type = tipo;
  }

  /** Exporta la tabla a Excel con la métrica y el semáforo que se ven en pantalla */
  public exportar(): void {
    if (!this.filas.length) return;
    const porcentaje = this.metrica === 'porcentaje';
    const numero = (valor: number | null) => valor == null ? '' : (porcentaje ? valor / this.maximoPonderacion : valor);
    const colorCelda = (valor: number | null) => {
      const sem = this.semaforoDe(valor);
      return sem && (this.semaforoFiltro === 'todos' || this.semaforoFiltro === sem.color) ? sem.color : '';
    };
    const global = this.translate.instant('cues.analisis-clima.global');
    const encabezados = [this.var1Sel, ...this.columnas, global];
    const formato = porcentaje ? FormatText.PORCENTAJE : FormatText.NUMBERDEC;
    const formatos = [FormatText.TEXT, ...this.columnas.map(() => formato), formato];
    const alineacion = [Aligment.LEFT, ...this.columnas.map(() => Aligment.CENTER), Aligment.CENTER];

    const datos: any[][] = this.matriz.map((fila, i) => [this.filas[i], ...fila.map(numero), numero(this.global[i])]);
    const colores: string[][] = this.matriz.map(fila => ['', ...fila.map(colorCelda), '']);
    const resaltadas: boolean[] = this.matriz.map(() => false);

    datos.push([this.translate.instant('cues.analisis-clima.promedio'), ...this.promedio.map(numero), numero(this.promedioGlobal)]);
    colores.push(encabezados.map(() => ''));
    resaltadas.push(true);

    this.excelService.generateExcelSinTitulo(encabezados, datos, `analisis-combinado_${this.dimSel}`, alineacion, formatos, colores, resaltadas);
  }

  /** Restaura filtros, métrica, semáforo y gráfica a como abre el tablero */
  public async borrarFiltros(): Promise<void> {
    this.cerrarModales();
    this.var1Key = '';
    this.var2Key = '';
    this.dimSel = '';
    this.metrica = 'calificacion';
    this.semaforoFiltro = 'todos';
    this.type = typesApex.LINE;
    this.etiquetasZona1 = true;

    // Los periodos vienen ordenados de menor a mayor: el último es el más reciente
    const reciente = this.periodosPropios[this.periodosPropios.length - 1];
    if (!reciente) return;
    this.allocationSelect = reciente.allocationId;
    this.evalSel = this.etiquetaPeriodo(reciente);
    this.marcarPeriodo(reciente.allocationId);
    await this.cargarResultados(reciente.allocationId);
  }

  public exportarGrafica(): void {
    if (!this.graficador) return;
    const titulos = {
      titulo: 'cues.analisis-clima.titulo-zona',
      subtitulo: `${this.var1Sel} · ${this.var2Sel} · ${this.dimSel}`,
      nombreArch: `analisis-combinado_${this.dimSel}`,
    };
    this.graficador.exportImg(1, titulos);
  }
}
